import React, { useState } from "react";
import { Plus } from "lucide-react";
import { XIcon } from "../../../components/icons";
import SubjectVariableEditor from "./SubjectVariableEditor";
import Toast from "../../../common/Toast/Toast";
import {
  CARD_CLASS,
  INPUT_CLASS,
  LABEL_CLASS,
  PRIMARY_BUTTON_CLASS,
} from "../../../common/CertificateOnboarding/utils/styles";
import {
  createDefaultVariableAttrs,
  normalizeTemplateVariables,
  syncVariableKeyFields,
  validateNewVariableKey,
} from "../../../common/CertificateOnboarding/utils/variables";

function AddDataAttributeCard({ variableConfig, onAdd, t }) {
  const [isOpen, setIsOpen] = useState(false);
  const [draftKey, setDraftKey] = useState("");
  const [draftAttrs, setDraftAttrs] = useState(() =>
    createDefaultVariableAttrs(),
  );
  const [keyError, setKeyError] = useState("");

  const resetForm = () => {
    setDraftKey("");
    setDraftAttrs(createDefaultVariableAttrs());
    setKeyError("");
    setIsOpen(false);
  };

  const handleOpen = () => {
    setIsOpen(true);
  };

  const handleDraftKeyChange = (value) => {
    setDraftKey(value);
    setKeyError("");
    setDraftAttrs((prev) => syncVariableKeyFields(prev, value.trim()));
  };

  const handleSubmit = () => {
    const trimmedKey = draftKey.trim();
    const validationError = validateNewVariableKey(trimmedKey, variableConfig);

    if (validationError) {
      setKeyError(validationError);
      const messageKey = {
        empty: "add_data_attribute_error_empty_key",
        invalid: "add_data_attribute_error_invalid_key",
        duplicate: "add_data_attribute_error_duplicate",
        reserved: "add_data_attribute_error_reserved",
      }[validationError];

      Toast({
        type: "error",
        title: t("certificateOnboarding:error"),
        message: t(`certificateOnboarding:${messageKey}`),
      });
      return;
    }

    const normalized = normalizeTemplateVariables({
      [trimmedKey]: syncVariableKeyFields(draftAttrs, trimmedKey),
    })[trimmedKey];

    onAdd(trimmedKey, normalized);
    Toast({
      type: "success",
      title: t("certificateOnboarding:success"),
      message: t("certificateOnboarding:add_data_attribute_success"),
    });
    resetForm();
  };

  if (!isOpen) {
    return (
      <button
        type="button"
        id="cert-onboarding-add-data-attribute"
        onClick={handleOpen}
        className={`${CARD_CLASS} w-full flex items-center justify-center gap-2.5 px-4 py-4 border-2 border-dashed border-cfPrimary/35 bg-cfPrimaryMuted/30 text-cfPrimary font-medium text-sm hover:border-cfPrimary hover:bg-cfPrimaryMuted/50 transition-colors duration-200 focus-visible:ring-2 focus-visible:ring-cfPrimary`}
      >
        <Plus className="w-5 h-5 shrink-0" aria-hidden />
        {t("certificateOnboarding:add_data_attribute")}
      </button>
    );
  }

  return (
    <div
      id="cert-onboarding-add-data-attribute-form"
      className={`${CARD_CLASS} border-2 border-cfPrimary/30 ring-1 ring-cfPrimary/10 overflow-hidden`}
    >
      <div className="flex items-start justify-between gap-3 px-4 py-4 sm:px-5 border-b border-cfCardBorder bg-cfPrimaryMuted/40">
        <div className="min-w-0">
          <h3 className="text-sm font-semibold text-cfHeadingText">
            {t("certificateOnboarding:add_data_attribute_title")}
          </h3>
          <p className="text-xs text-cfGlobalText mt-1 leading-relaxed">
            {t("certificateOnboarding:add_data_attribute_subtitle")}
          </p>
        </div>
        <button
          type="button"
          onClick={resetForm}
          className="shrink-0 inline-flex h-8 w-8 items-center justify-center rounded-lg text-cfGlobalText hover:bg-cfSecondary hover:text-cfHeadingText transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-cfPrimary"
          aria-label={t("certificateOnboarding:add_data_attribute_cancel")}
        >
          <XIcon size={16} className="inline-flex" aria-hidden />
        </button>
      </div>

      <div className="px-4 py-4 sm:px-5 sm:py-5 space-y-5 bg-cfSecondary/30">
        <div className="space-y-1.5">
          <label
            htmlFor="cert-onboarding-new-variable-key"
            className={LABEL_CLASS}
          >
            {t("certificateOnboarding:attr_variable_key")}
          </label>
          <input
            id="cert-onboarding-new-variable-key"
            type="text"
            value={draftKey}
            onChange={(e) => handleDraftKeyChange(e.target.value)}
            placeholder={t(
              "certificateOnboarding:attr_variable_key_placeholder",
            )}
            className={`${INPUT_CLASS} ${
              keyError ? "border-cfStatusBlockedText" : ""
            }`}
          />
          <p className="text-xs text-cfGlobalText">
            {t("certificateOnboarding:attr_variable_key_hint")}
          </p>
        </div>

        {draftKey.trim() && (
          <SubjectVariableEditor
            variableKey={draftKey.trim()}
            attrs={draftAttrs}
            setVariableConfig={(updater) => {
              setDraftAttrs((prev) => {
                const key = draftKey.trim();
                const draftConfig = { [key]: prev };
                const nextConfig =
                  typeof updater === "function"
                    ? updater(draftConfig)
                    : updater;
                return nextConfig[key] || prev;
              });
            }}
            t={t}
          />
        )}

        <div className="flex flex-col-reverse sm:flex-row gap-3 pt-1">
          <button
            type="button"
            onClick={resetForm}
            className="flex-1 px-4 py-2.5 border border-cfCardBorder text-cfHeadingText font-medium rounded-lg hover:bg-cfCardHover transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-cfPrimary"
          >
            {t("certificateOnboarding:add_data_attribute_cancel")}
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!draftKey.trim()}
            className={`${PRIMARY_BUTTON_CLASS} flex-1 py-2.5 rounded-lg`}
          >
            {t("certificateOnboarding:add_data_attribute_submit")}
          </button>
        </div>
      </div>
    </div>
  );
}

export default AddDataAttributeCard;
